import React, { useState } from 'react';
import { apiService } from '../../../services/api';

interface OrganizerData {
  id: number;
  name: string;
  slug: string;
  description?: string;
  logo_url?: string;
  website?: string;
  email?: string;
  facebook?: string;
  instagram?: string;
  twitter?: string;
  linkedin?: string;
  youtube?: string;
  tiktok?: string;
}

interface EventData {
  id: number;
  slug: string;
  setting?: {
    allow_memories?: boolean;
  };
}

interface OrganizerProps {
  organizer: OrganizerData | null;
  event?: EventData;
}

const Organizer: React.FC<OrganizerProps> = ({ organizer, event }) => {
  const [isStartingChat, setIsStartingChat] = useState(false);
  const [isJoiningDiscussion, setIsJoiningDiscussion] = useState(false);

  if (!organizer) {
    return null;
  }

  const socialLinks = [
    { key: 'facebook', icon: 'fab fa-facebook-f', label: 'Facebook', url: organizer.facebook },
    { key: 'instagram', icon: 'fab fa-instagram', label: 'Instagram', url: organizer.instagram },
    { key: 'twitter', icon: 'fab fa-twitter', label: 'Twitter', url: organizer.twitter },
    { key: 'linkedin', icon: 'fab fa-linkedin-in', label: 'LinkedIn', url: organizer.linkedin },
    { key: 'youtube', icon: 'fab fa-youtube', label: 'YouTube', url: organizer.youtube },
    { key: 'tiktok', icon: 'fab fa-tiktok', label: 'TikTok', url: organizer.tiktok },
  ].filter(link => link.url);

  const handleSocialClick = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleChatWithOrganizer = async () => {
    if (!isStartingChat) {
      try {
        setIsStartingChat(true);
        console.log('Event data for chat:', event);
        const response = await apiService.chat.startEventChat(event?.id || 0, 'private');
        
        if (response.success && response.chat_room) {
          // Navigate to the chat room
          window.location.href = `/chat/${response.chat_room.id}`;
        } else {
          console.error('Failed to start chat with organizer');
          // Fallback to Django URL if API fails
          window.location.href = `/chat/event/${event?.id || 0}/start/`;
        }
      } catch (error) {
        console.error('Error starting chat with organizer:', error);
        // Fallback to Django URL if API fails
        window.location.href = `/chat/event/${event?.id || 0}/start/`;
      } finally {
        setIsStartingChat(false);
      }
    }
  };

  const handleJoinDiscussion = async () => {
    if (!isJoiningDiscussion) {
      try {
        setIsJoiningDiscussion(true);
        const response = await apiService.chat.startEventChat(event?.id || 0, 'public');
        
        if (response.success && response.chat_room) {
          // Navigate to the chat room
          window.location.href = `/chat/${response.chat_room.id}`;
        } else {
          console.error('Failed to join event discussion');
          // Fallback to Django URL if API fails
          window.location.href = `/chat/event/${event?.id || 0}/join/`;
        }
      } catch (error) {
        console.error('Error joining event discussion:', error);
        // Fallback to Django URL if API fails
        window.location.href = `/chat/event/${event?.id || 0}/join/`;
      } finally {
        setIsJoiningDiscussion(false);
      }
    }
  };

  const handleEventMemories = () => {
    if (event?.slug) {
      // Navigate to event memories
      window.location.href = `/events/${event.slug}/memories/`;
    }
  };

  const handleViewAllEvents = () => {
    // Navigate to organizer's microsite
    window.location.href = `/${organizer.slug}/`;
  };

  const getWebsiteDisplayName = (url: string) => {
    return url
      .replace(/^https?:\/\//, '')
      .replace(/^www\./, '')
      .replace(/\/$/, '');
  };

  return (
    <div className="organizer-section">
      <div className="organizer-content">
        <div className="organizer-logo">
          {organizer.logo_url ? (
            <img 
              src={organizer.logo_url} 
              alt={`${organizer.name} logo`}
              loading="eager"
              decoding="async"
              onLoad={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.opacity = '1';
              }}
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
                const parent = target.parentElement;
                if (parent) {
                  parent.innerHTML = `
                    <div class="no-logo">
                      <i class="fas fa-building"></i>
                      <span>${organizer.name.slice(0, 2).toUpperCase()}</span>
                    </div>
                  `;
                }
              }}
              style={{ opacity: 0, transition: 'opacity 0.3s ease' }}
            />
          ) : (
            <div className="no-logo">
              <i className="fas fa-building"></i>
              <span>{organizer.name.slice(0, 2).toUpperCase()}</span>
            </div>
          )}
        </div>

        <div className="organizer-info">
          <h3>
            <span className="organizer-label">Organized by</span>
            <span className="organizer-name">{organizer.name}</span>
          </h3>
          
          {organizer.description && (
            <p dangerouslySetInnerHTML={{ __html: organizer.description }}></p>
          )}

          <div className="organizer-actions">
            <button 
              className="btn btn-outline-primary chat-with-organizer"
              onClick={handleChatWithOrganizer}
              disabled={isStartingChat}
            >
              <i className={`fas ${isStartingChat ? 'fa-spinner fa-spin' : 'fa-comments'}`}></i>
              <span>{isStartingChat ? 'Starting Chat...' : 'Chat with Organizer'}</span>
            </button>
            
            <button 
              className="btn btn-outline-secondary"
              onClick={handleJoinDiscussion}
              disabled={isJoiningDiscussion}
            >
              <i className={`fas ${isJoiningDiscussion ? 'fa-spinner fa-spin' : 'fa-users'}`}></i>
              <span>{isJoiningDiscussion ? 'Joining...' : 'Join Event Discussion'}</span>
            </button>
            
            {event?.setting?.allow_memories && (
              <button 
                className="btn btn-outline-success"
                onClick={handleEventMemories}
              >
                <i className="fas fa-images"></i>
                <span>Event Memories</span>
              </button>
            )}
            
            <button 
              className="btn btn-outline-primary"
              onClick={handleViewAllEvents}
            >
              <i className="fas fa-calendar-alt"></i>
              <span>View All Events</span>
            </button>
          </div>

          {(organizer.email || organizer.website) && (
            <div className="organizer-details">
              {organizer.email && (
                <div className="detail-item">
                  <i className="fas fa-envelope"></i>
                  <a 
                    href={`mailto:${organizer.email}`} 
                    title={`Send email to ${organizer.name}`}
                  >
                    {organizer.email}
                  </a>
                </div>
              )}
              
              {organizer.website && (
                <div className="detail-item">
                  <i className="fas fa-globe"></i>
                  <a 
                    href={organizer.website} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    title={`Visit ${organizer.name} website`}
                  >
                    {getWebsiteDisplayName(organizer.website)}
                  </a>
                </div>
              )}
            </div>
          )}

          {socialLinks.length > 0 && (
            <div className="social-links">
              {socialLinks.map((link) => (
                <a
                  key={link.key}
                  href={link.url}
                  className="btn btn-primary"
                  target="_blank"
                  rel="noopener noreferrer"
                  title={`Follow ${organizer.name} on ${link.label}`}
                >
                  <i className={link.icon}></i>
                </a>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Organizer;
